import React, { createContext, useContext, useState, ReactNode } from "react";
import {
  useSignalR,
  useSheshaApplication,
  useLocalStorage,
  useGlobalState,
  useConfigurableAction,
  FormMarkup,
} from "@shesha-io/reactjs";

import {
  ITelephonyProvider,
  TelephonyProviderState,
  TelephonyContextType,
  CallStatus,
  InnovaphoneConfig,
  AwsConnectConfig,
  ITelePhonyAgent,
} from "../../models/index";
import { InnovaphoneProvider } from "../../providers/innovaphoneProvider";

import { AwsConnectProvider } from "../../providers/awsConnectProvider";

import { httpGet } from "../../services/httpService";

import settingsJson from "./dynamic-action-settings.json";
import settingsJsonAgent from "./dynamic-action-settings-agent.json";
// Utility function to get the current provider
const getCurrentProvider = (
  provider: ITelephonyProvider | null,
  globalState: any,
): ITelephonyProvider | null => {
  return (
    provider ?? (globalState.telephonyProvider as ITelephonyProvider) ?? null
  );
};

const settingsMarkup = settingsJson as FormMarkup;
const settingsMarkupAgent = settingsJsonAgent as FormMarkup;

export interface IDialActionArgs {
  phoneNumber: string;
}

export interface ITransferActionArgs {
  phoneNumber: string;
  agentId: string;
}

// Default state
const defaultState: TelephonyProviderState = {
  isLoggedIn: false,
  isAvailable: false,
  status: CallStatus.Idle,
  sessionId: "",
};

// Create context
const TelephonyContext = createContext<TelephonyContextType>({
  getTelephonyAgent: async () => null,
  provider: null,
  providerState: defaultState,
  initialize: async () => false,
  isConnectionReady: false,
});

interface TelephonyProviderProps {
  children: ReactNode;
}

export const CONFIGURABLE_ACTIONS_OWNER = "Telephony Actions";
export const CONFIGURABLE_ACTIONS_NAME = "Dial";

export const TelephonyProvider: React.FC<TelephonyProviderProps> = ({
  children,
}) => {
  const [isLoggedIn, setIsLoggedIn] = useLocalStorage(
    `Telephony:isLoggedIn`,
    false,
  );
  console.log("isLoggedIn from localStorage", isLoggedIn);
  //set the default state
  //defaultState.isLoggedIn = isLoggedIn;

  const [provider, setProvider] = useState<ITelephonyProvider | null>(null);
  const [providerState, setProviderState] =
    useState<TelephonyProviderState>(defaultState);
  //const [stateUpdateInterval, setStateUpdateInterval] = useState<NodeJS.Timeout | null>(null);

  const { connection } = useSignalR(false) ?? {};

  const { backendUrl, httpHeaders } = useSheshaApplication();

  const { globalState, setState } = useGlobalState();

  // Function to create provider instance based on type
  const createProvider = (
    providerType: string,
    config: InnovaphoneConfig | AwsConnectConfig,
  ): ITelephonyProvider | null => {
    switch (providerType) {
      case "innovaphone":
        config.apiBaseUrl = backendUrl;
        return new InnovaphoneProvider(
          config as InnovaphoneConfig,
          connection,
          httpHeaders,
        );
      case "awsconnect":
        return new AwsConnectProvider(
          config as AwsConnectConfig,
          connection,
          isLoggedIn,
        );
      default:
        console.error(`Unknown provider type: ${providerType}`);
        return null;
    }
  };

  const getTelephonyAgent = async (): Promise<ITelePhonyAgent | null> => {
    try {
      var response = await httpGet(
        `${backendUrl}/api/services/Telephony/TelephonyAgentActions/GetAgent`,
        httpHeaders,
      );
      if (response?.result) {
        return {
          username: response.result.username,
          password: response.result.password,
          hasInbound: response.result.hasInbound,
          hasOutbound: response.result.hasOutbound,
        };
      }
    } catch (error) {
      console.error("Error fetching telephony agent:", error);
    }
    return null;
  };

  // Initialize the telephony provider
  const initialize = async (
    providerType: string,
    config: any,
  ): Promise<boolean> => {
    const newProvider = createProvider(providerType, config);
    if (!newProvider) return false;

    setProvider(newProvider);
    setProviderState(newProvider.getState());
    setState({ key: "telephonyProvider", data: newProvider });

    //get the telphony agent details

    // Register for state updates
    const setupEventListeners = () => {
      // Register for all events to update state
      const updateState = () => {
        if (newProvider) {
          var currentState = newProvider.getState();
          setProviderState(currentState);
          setIsLoggedIn(currentState.isLoggedIn);
        }
      };

      newProvider.onIncomingCallRinging(updateState);
      newProvider.onIncomingCallAnswered(updateState);
      newProvider.onOutboundCallInitiated(updateState);
      newProvider.onOutboundCallAnswered(updateState);
      newProvider.onCallTransferred(updateState);
      newProvider.onCallEnded(updateState);
      newProvider.onIncomingCallRejected(updateState);
      newProvider.onOutgoingCallRejected(updateState);
      newProvider.onAvailabilityChanged(updateState);
      newProvider.onLoggedInChanged(updateState);
      newProvider.onStatusChanged(updateState);

      // // Set up polling for state updates - especially for Innovaphone integration
      // // as the event-based system may not catch all state changes
      // if (providerType === 'innovaphone') {
      //     if (stateUpdateInterval) {
      //         clearInterval(stateUpdateInterval);
      //     }

      //     const interval = setInterval(() => {
      //         if (newProvider) {
      //             setProviderState(newProvider.getState());
      //         }
      //     }, 5000); // Update every 5 seconds

      //     setStateUpdateInterval(interval);
      // }
    };

    setupEventListeners();

    return true;
  };

  // useEffect(() => {
  //     // Clean up on unmount
  //     return () => {
  //         if (provider) {
  //             // provider.logout();
  //         }

  //         if (stateUpdateInterval) {
  //             clearInterval(stateUpdateInterval);
  //         }
  //     };
  // }, [provider, stateUpdateInterval]);

  useConfigurableAction<IDialActionArgs>(
    {
      name: "Dial Number",
      owner: CONFIGURABLE_ACTIONS_OWNER,
      ownerUid: CONFIGURABLE_ACTIONS_OWNER,
      argumentsFormMarkup: settingsMarkup,
      hasArguments: true,
      executer: (actionArgs) => {
        console.log("Dial action executed");
        const { phoneNumber } = actionArgs;
        //refreshTable(); // TODO: return correct promise
        console.log("provider in dial action", provider);
        console.log("provider in dial globalState", globalState);

        const providerToUse = getCurrentProvider(provider, globalState);
        providerToUse?.dial(phoneNumber);
        return Promise.resolve();
      },
    },
    [provider, globalState],
  );

  useConfigurableAction(
    {
      name: "End Active Call",
      owner: CONFIGURABLE_ACTIONS_OWNER,
      ownerUid: CONFIGURABLE_ACTIONS_OWNER,
      argumentsFormMarkup: settingsMarkup,
      hasArguments: false,
      executer: () => {
        console.log("End Call action executed");
        const providerToUse = getCurrentProvider(provider, globalState);
        providerToUse?.endCall();
        return Promise.resolve();
      },
    },
    [provider, globalState],
  );

  useConfigurableAction<ITransferActionArgs>(
    {
      name: "Transfer Active Call",
      owner: CONFIGURABLE_ACTIONS_OWNER,
      ownerUid: CONFIGURABLE_ACTIONS_OWNER,
      argumentsFormMarkup: settingsMarkupAgent,
      hasArguments: true,
      executer: (actionArgs) => {
        console.log("Transfer action executed");
        const { phoneNumber, agentId } = actionArgs;
        //refreshTable(); // TODO: return correct promise
        console.log("provider in transfer action", provider);
        console.log("provider in transfer globalState", globalState);

        const providerToUse = getCurrentProvider(provider, globalState);
        if (!providerToUse) {
          return Promise.reject(
            new Error("No telephony provider available for transfer"),
          );
        }

        const transferTarget = agentId || phoneNumber;
        if (!transferTarget) {
          return Promise.reject(
            new Error("Transfer requires either an agentId or a phoneNumber"),
          );
        }

        const isAgent = !!agentId;
        return providerToUse.transferCall(transferTarget, isAgent);
      },
    },
    [provider, globalState],
  );

  return (
    <TelephonyContext.Provider
      value={{
        getTelephonyAgent,
        provider,
        providerState,
        initialize,
        isConnectionReady: !!connection,
      }}
    >
      {children}
    </TelephonyContext.Provider>
  );
};

// Custom hook to use telephony context
export const useTelephonyContext = () => useContext(TelephonyContext);
